import React, { useMemo, useState } from "react";
import { isPlainObject } from "lodash";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  LinkOff as LinkOffIcon,
  Edit as EditIcon,
  FolderOff as FolderOffIcon,
} from "@mui/icons-material";
import {
  isTextConnectedField,
  getTextWidgetMetrics,
  TEXT_CONNECTION_FONT_FAMILY,
} from "../../../../../../../utils/textFieldConnections";
import { FavouriteWidgetDropTarget } from "../FavouriteDragAndDrop";
import { computeFitFontSize } from "./fitOverlayText";

const GENERIC_FIELD_TYPES = new Map([
  ["generic_text", "Text"],
  ["generic_number", "Number"],
  ["generic_dollar", "Dollar"],
  ["generic_date", "Date"],
  ["generic_multiline", "Multiline"],
  ["generic_email", "Email"],
  ["generic_phone", "Mobile"],
  ["generic_money", "Money"],
  ["generic_todaydate", "Today Date"],
  ["generic_full_title", "Full Title"],
  ["generic_full_subtitle", "Full Sub Title"],
  ["generic_total", "Total"],
]);

const FieldOverlay = ({
  field,
  rect,
  checkboxRect,
  isDrawing,
  isSelected,
  selectToolActive,
  typingToolActive,
  highlightFields,
  textOverlayDisplayMode,
  focusedField,
  currCursor,
  isUnassigned,
  fixedInputs,
  onFocus,
  onBlur,
  onMouseDown,
  onChange,
  onClick,
  onCheckboxOverlayClick,
  activeEditorTool,
  setCurrentProcessingFile,
  isPreview,
  setOpenEditBlockDialog,
  setSelectedBlock,
  formField,
  shouldMarkCircle = false,
  isMultiline: catalogIsMultiline = false,
  isPreviewHovered = false,
  onFavouriteDrop,
  favouriteDropDisabled = false,
}) => {
  const isCheckboxLike =
    field.type === "PDFCheckBox" || field.type === "PDFRadioGroup";
  const isHandToolActive = !selectToolActive && !typingToolActive;
  const textFieldCursor = selectToolActive
    ? currCursor
    : fixedInputs.some((prefix) => field.name.startsWith(prefix))
      ? "default"
      : "text";
  const checkboxCursor = selectToolActive
    ? currCursor
    : isHandToolActive
      ? currCursor || "grab"
      : "pointer";
  const assignedBorderColor = formField?.is_default ? "#1976d2" : "#2e7d32";
  const previewHoverBorderColor = "#ed6c02";

  const isBlock = useMemo(
    () => field?.name?.startsWith("block__"),
    [field?.name],
  );

  const genericFieldType = GENERIC_FIELD_TYPES.get(
    String(field?.field_id ?? "")
      .trim()
      .toLowerCase(),
  );
  const isGenericField = Boolean(genericFieldType);

  const [openUnassignFieldDialog, setOpenUnassignFieldDialog] = useState(false);
  const isConnectedTextPreview = isPreview && isTextConnectedField(field);

  const textMetrics = isConnectedTextPreview
    ? getTextWidgetMetrics(field)
    : null;

  const isMultiline = catalogIsMultiline || isConnectedTextPreview;

  // Font size for the value: shrink to fit the widget when the widget is
  // narrower than the text. Kept at field.fontSize whenever it already fits.
  const baseFontSize = Number(field.fontSize) || 12;

  const fittedFontSize = useMemo(() => {
    // Checkbox/radio widgets don't render a value here — they use the ✕ mark
    // below, which already scales by rect size.
    if (isCheckboxLike || shouldMarkCircle) return baseFontSize;

    const text = String(
      isPlainObject(field.value)
        ? (field.value?.label ?? "")
        : (field.value ?? ""),
    );

    return computeFitFontSize(
      text,
      Math.max(rect.width - 4, 1),
      Math.max(rect.height - 2, 1),
      {
        min: 5,
        max: baseFontSize,
        step: 0.25,
        fontFamily: "Helvetica, Arial, sans-serif",
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCheckboxLike,
    shouldMarkCircle,
    field.value,
    baseFontSize,
    rect.width,
    rect.height,
  ]);

  const TextInputComponent = isMultiline ? "textarea" : "input";

  const handleCloseUnassignFieldDialog = () => {
    setOpenUnassignFieldDialog(false);
  };

  const handleUnassign = () => {
    setCurrentProcessingFile((prevFile) => {
      const tempFields = prevFile?.document?.tempFields;
      const updatedFormFields = tempFields?.formFields?.map((f) => {
        if (field?.name === f?.name) {
          return {
            ...f,
            field_id: "",
            location: "",
          };
        } else {
          return f;
        }
      });

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            formFields: updatedFormFields,
          },
        },
      };
    });

    handleCloseUnassignFieldDialog();
  };

  const handleUntagFromSection = () => {
    setCurrentProcessingFile((prevFile) => {
      const tempFields = prevFile?.document?.tempFields;
      const updatedFormFields = tempFields?.formFields?.map((f) =>
        field?.name === f?.name
          ? {
              ...f,
              block_id: "",
              block_label: "",
            }
          : f,
      );

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            formFields: updatedFormFields,
          },
        },
      };
    });
  };

  return (
    <>
      {!isPreview &&
        !isBlock &&
        typeof onFavouriteDrop === "function" &&
        ["PDFTextField", "PDFText", "PDFCheckBox", "PDFRadioGroup"].includes(
          field.type,
        ) && (
          <FavouriteWidgetDropTarget
            widgetName={field.name}
            rect={isCheckboxLike ? checkboxRect : rect}
            disabled={favouriteDropDisabled || isDrawing}
            onAssign={onFavouriteDrop}
          />
        )}
      {shouldMarkCircle && (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            left: `${rect.left - 2}px`,
            top: `${rect.top - 2}px`,
            width: `${rect.width + 4}px`,
            height: `${rect.height + 4}px`,
            border: "2px solid #111",
            borderRadius: "50%",
            boxSizing: "border-box",
            pointerEvents: "none",
            zIndex: 57,
          }}
        />
      )}

      {isCheckboxLike && (
        <Tooltip
          title={
            <Box
              display="flex"
              flexDirection={"column"}
              alignItems={"center"}
              gap={"6px"}
            >
              {/* Label */}
              <Typography
                sx={{ fontSize: "12px", fontWeight: 500, color: "#fff" }}
              >
                {isGenericField
                  ? genericFieldType
                  : formField?.field_label ||
                    field.field_label ||
                    field.label ||
                    field.name}
              </Typography>

              {/* Section Name */}
              {(field?.block_id || field?.block_label) && (
                <Typography
                  sx={{ fontSize: "11px", fontWeight: 500, color: "#fff" }}
                >
                  {`Section: ${field?.block_label || formField?.block_label || "N/A"}`}
                </Typography>
              )}

              {/* Action Buttons */}
              <Box
                display={"flex"}
                alignItems={"center"}
                gap={"8px"}
                justifyContent={"center"}
              >
                <Tooltip title="Untag Field" arrow placement="bottom">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setOpenUnassignFieldDialog(true);
                    }}
                    sx={{
                      border: "1px solid #d0d7de",
                      borderRadius: 1.5,
                      backgroundColor: "transparent",
                      p: "4px !important",
                    }}
                  >
                    <LinkOffIcon sx={{ fontSize: "1rem", fill: "#fff" }} />
                  </IconButton>
                </Tooltip>

                {(field?.block_id || field?.block_label) && (
                  <Tooltip title="Detach from Section" arrow placement="bottom">
                    <IconButton
                      size="small"
                      onClick={handleUntagFromSection}
                      sx={{
                        border: "1px solid #d0d7de",
                        borderRadius: 1.5,
                        backgroundColor: "transparent",
                        p: "4px !important",
                      }}
                    >
                      <FolderOffIcon sx={{ fontSize: "1rem", fill: "#fff" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          }
          arrow
          placement="right"
          slotProps={{
            popper: { sx: { display: field?.field_id ? "block" : "none" } },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: `${checkboxRect.left}px`,
              top: `${checkboxRect.top}px`,
              width: `${checkboxRect.width}px`,
              height: `${checkboxRect.height}px`,
              backgroundColor:
                highlightFields || focusedField === field.name
                  ? "RGBA(255, 211, 0, 0.3)"
                  : "transparent",
              zIndex: 56,
              cursor:
                isPreview && selectToolActive ? "default" : checkboxCursor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#111",
              fontSize: `${Math.max(Math.min(checkboxRect.width, checkboxRect.height) * 0.8, 10)}px`,
              fontWeight: 700,
              lineHeight: 1,
              userSelect: "none",
              pointerEvents: "auto",
              outline: field.outline
                ? `${field?.lineWidth ?? 1}px solid #000000`
                : "none",
              boxShadow:
                !isPreview && field?.field_id
                  ? `inset 0 0 0 2px ${assignedBorderColor}`
                  : "none",
            }}
            onClick={onCheckboxOverlayClick}
          >
            {!shouldMarkCircle &&
            (isPlainObject(field.value)
              ? field.value?.label?.toLowerCase() !== "no"
              : field.value)
              ? "✕"
              : ""}
          </Box>
        </Tooltip>
      )}

      {(() => {
        const isLabelableField = [
          "PDFTextField",
          "PDFCheckBox",
          "PDFRadioGroup",
        ].includes(field.type);
        const showOverlayText =
          isLabelableField &&
          selectToolActive &&
          highlightFields &&
          textOverlayDisplayMode !== "none";
        const overlayText =
          textOverlayDisplayMode === "name"
            ? field.name
            : field.field_label || field.label || field.name;

        return showOverlayText ? (
          <Box
            sx={{
              position: "absolute",
              left: `${rect.left}px`,
              top: `${rect.top}px`,
              transform: isCheckboxLike
                ? "translateY(calc(-100% + 2px))"
                : "none",
              width: isCheckboxLike ? "max-content" : `${rect.width}px`,
              maxWidth: isCheckboxLike ? "420px" : `${rect.width}px`,
              minWidth: isCheckboxLike ? "max-content" : `${rect.width}px`,
              height: isCheckboxLike ? "auto" : `${rect.height}px`,
              display: "flex",
              alignItems: isCheckboxLike ? "flex-end" : "center",
              justifyContent: isCheckboxLike ? "flex-start" : "center",
              color: "#023ce9",
              fontSize: "12px",
              fontWeight: "bold",
              borderRadius: "2px",
              pointerEvents: "none",
              zIndex: isSelected ? 61 : 60,
              overflow: isCheckboxLike ? "visible" : "hidden",
              textOverflow: isCheckboxLike ? "clip" : "ellipsis",
              whiteSpace: isCheckboxLike ? "normal" : "nowrap",
              wordBreak: "break-word",
              padding: "2px",
              backgroundColor: "transparent",
              lineHeight: 1.2,
              textAlign: isCheckboxLike ? "left" : "center",
            }}
          >
            {overlayText}
          </Box>
        ) : null;
      })()}

      {isPreview &&
        selectToolActive &&
        field.type === "PDFTextField" &&
        Number.isFinite(Number(field.order)) && (
          <Box
            aria-label={`Field order ${field.order}`}
            sx={{
              position: "absolute",
              left: `${rect.left}px`,
              top: `${rect.top}px`,
              width: `${rect.width}px`,
              height: `${rect.height}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 102,
            }}
          >
            <Box
              component="span"
              sx={{
                minWidth: 20,
                px: 0.5,
                py: 0.25,
                borderRadius: 1,
                color: "#fff",
                backgroundColor: isPreviewHovered
                  ? previewHoverBorderColor
                  : "rgba(25, 118, 210, 0.9)",
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
                textAlign: "center",
              }}
            >
              {field.order}
            </Box>
          </Box>
        )}

      {["PDFTextField", "PDFCheckBox", "PDFRadioGroup", "PDFText"].includes(
        field.type,
      ) ? (
        <Tooltip
          title={
            <Box
              display="flex"
              flexDirection={"column"}
              alignItems={"center"}
              gap={"6px"}
            >
              {/* Label */}
              <Typography
                sx={{ fontSize: "12px", fontWeight: 500, color: "#fff" }}
              >
                {isGenericField
                  ? genericFieldType
                  : formField?.field_label ||
                    field.field_label ||
                    field.label ||
                    field.name}
              </Typography>

              {/* Section Name */}
              {(field?.block_id || field?.block_label) && (
                <Typography
                  sx={{ fontSize: "11px", fontWeight: 500, color: "#fff" }}
                >
                  {`Section: ${field?.block_label || formField?.block_label || "N/A"}`}
                </Typography>
              )}

              {/* Action Buttons */}
              <Box
                display={"flex"}
                alignItems={"center"}
                gap={"8px"}
                justifyContent={"center"}
              >
                <Tooltip title="Untag Field" arrow placement="bottom">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setOpenUnassignFieldDialog(true);
                    }}
                    sx={{
                      border: "1px solid #d0d7de",
                      borderRadius: 1.5,
                      backgroundColor: "transparent",
                      p: "4px !important",
                    }}
                  >
                    <LinkOffIcon sx={{ fontSize: "1rem", fill: "#fff" }} />
                  </IconButton>
                </Tooltip>

                {(field?.block_id || field?.block_label) && (
                  <Tooltip title="Detach from Section" arrow placement="bottom">
                    <IconButton
                      size="small"
                      onClick={handleUntagFromSection}
                      sx={{
                        border: "1px solid #d0d7de",
                        borderRadius: 1.5,
                        backgroundColor: "transparent",
                        p: "4px !important",
                      }}
                    >
                      <FolderOffIcon sx={{ fontSize: "1rem", fill: "#fff" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          }
          arrow
          placement="right"
          slotProps={{
            popper: { sx: { display: field?.field_id ? "block" : "none" } },
          }}
        >
          <TextInputComponent
            type={
              isMultiline
                ? undefined
                : field.type === "PDFCheckBox"
                  ? "checkbox"
                  : field.type === "PDFRadioGroup"
                    ? "radio"
                    : field.name.startsWith("date_field")
                      ? "date"
                      : "text"
            }
            value={
              field.type === "PDFCheckBox" || field.type === "PDFRadioGroup"
                ? undefined
                : shouldMarkCircle
                  ? ""
                  : (isPlainObject(field.value)
                      ? field.value?.label
                      : field.value) || ""
            }
            checked={
              field.type === "PDFCheckBox" || field.type === "PDFRadioGroup"
                ? shouldMarkCircle
                  ? false
                  : field.value
                : undefined
            }
            autoFocus={field.type === "PDFText"}
            onFocus={onFocus}
            onMouseDown={onMouseDown}
            onBlur={onBlur}
            readOnly={
              (selectToolActive && field.type !== "PDFText") ||
              fixedInputs.some((prefix) => field.name.startsWith(prefix)) ||
              (activeEditorTool === "hand" && field.type === "PDFText") ||
              isPreview
            }
            onChange={onChange}
            onClick={onClick}
            style={{
              position: "absolute",
              fontSize: `${fittedFontSize}px`,
              left: `${rect.left}px`,
              top: `${rect.top}px`,
              width: `${rect.width}px`,
              height: `${rect.height}px`,
              border:
                field.type === "PDFCheckBox" || field.type === "PDFRadioGroup"
                  ? "none"
                  : field.type === "PDFText"
                    ? focusedField === field.name && activeEditorTool !== "hand"
                      ? "1px solid #000000"
                      : "none"
                    : isPreviewHovered
                      ? `2px solid ${previewHoverBorderColor}`
                      : !selectToolActive && !highlightFields
                        ? "none"
                        : isUnassigned &&
                            selectToolActive &&
                            field.type !== "PDFText"
                          ? field?.field_id
                            ? `2px solid ${assignedBorderColor}`
                            : "2px solid #d32f2f"
                          : typingToolActive ||
                              fixedInputs.some((prefix) =>
                                field.name.startsWith(prefix),
                              )
                            ? "none"
                            : field?.field_id
                              ? `2px solid ${assignedBorderColor}`
                              : "1px solid #000",
              outline: "none",
              backgroundColor:
                field.type === "PDFCheckBox" ||
                field.type === "PDFRadioGroup" ||
                field.type === "PDFText"
                  ? "transparent"
                  : focusedField === field.name
                    ? "transparent"
                    : highlightFields &&
                        fixedInputs.some((prefix) =>
                          field.name.startsWith(prefix),
                        )
                      ? "transparent"
                      : highlightFields
                        ? "RGBA(255, 211, 0, 0.3)"
                        : "transparent",
              zIndex: isCheckboxLike ? 55 : field.type === "PDFText" ? 101 : 50,
              cursor: isPreview
                ? "default"
                : isCheckboxLike
                  ? checkboxCursor
                  : textFieldCursor,
              textAlign: field.textAlign || field.alignment || "left",
              boxSizing: "border-box",
              paddingTop: "1px",
              paddingBottom: "0px",
              paddingLeft: "2px",
              paddingRight: "2px",
              lineHeight: `${Math.max(fittedFontSize, 10)}px`,
              pointerEvents: isDrawing
                ? "none"
                : isCheckboxLike
                  ? "none"
                  : "auto",
              appearance: isCheckboxLike ? "none" : undefined,
              WebkitAppearance: isCheckboxLike ? "none" : undefined,
              MozAppearance: isCheckboxLike ? "none" : undefined,
              boxShadow: isCheckboxLike ? "none" : undefined,
              borderRadius: isCheckboxLike ? 0 : undefined,
              resize: isMultiline ? "none" : undefined,
              whiteSpace: isMultiline ? "pre-wrap" : undefined,
              overflowWrap: isMultiline ? "break-word" : undefined,
              overflowY: isMultiline ? "auto" : undefined,
              fontFamily: isMultiline ? "inherit" : undefined,
              ...(isConnectedTextPreview
                ? {
                    fontFamily: TEXT_CONNECTION_FONT_FAMILY,
                    fontSize: `${textMetrics.fontSize}px`,
                    lineHeight: `${textMetrics.lineHeight}px`,
                    fontWeight: 400,
                    fontStyle: "normal",
                    letterSpacing: "0px",
                    wordSpacing: "0px",
                    textTransform: "none",
                    textIndent: 0,

                    paddingTop: "1px",
                    paddingBottom: "0px",
                    paddingLeft: "2px",
                    paddingRight: "2px",

                    // Borders must not reduce the space used for text measurement.
                    border: "none",
                    boxSizing: "border-box",
                    boxShadow: isPreviewHovered
                      ? `inset 0 0 0 2px ${previewHoverBorderColor}`
                      : "none",

                    // The helper inserts line breaks; prevent additional browser wrapping.
                    whiteSpace: "pre",
                    overflowWrap: "normal",
                    wordBreak: "normal",
                    overflow: "hidden",
                    resize: "none",
                  }
                : {}),
            }}
          />
        </Tooltip>
      ) : field.type === "PDFRectangle" ? (
        <Tooltip
          title={
            <Box
              display="flex"
              flexDirection={"column"}
              alignItems={"center"}
              gap={"6px"}
            >
              <Typography
                sx={{ fontSize: "12px", fontWeight: 500, color: "#fff" }}
              >{`${field?.block_label || "N/A"}`}</Typography>

              <Box
                display={"flex"}
                alignItems={"center"}
                gap={"4px"}
                justifyContent={"center"}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpenEditBlockDialog(true);
                    setSelectedBlock(field?.block_id);
                  }}
                  sx={{
                    border: "1px solid #d0d7de",
                    borderRadius: 1.5,
                    backgroundColor: "transparent",
                    p: "4px !important",
                  }}
                >
                  <EditIcon sx={{ fontSize: "1rem", fill: "#fff" }} />
                </IconButton>
              </Box>
            </Box>
          }
          arrow
          placement="right"
          slotProps={{
            popper: { sx: { display: isBlock ? "block" : "none" } },
          }}
          leaveDelay={600}
        >
          <Box
            sx={{
              width: `${rect.width}px`,
              height: `${rect.height}px`,
              backgroundColor: "transparent",
              outline: `${isBlock ? 2 : (field?.lineWidth ?? 1)}px solid`,
              outlineColor: isBlock ? "primary.main" : "#000",
              position: "absolute",
              left: `${rect.left}px`,
              top: `${rect.top}px`,
              zIndex: isBlock ? 10 : 55,
              pointerEvents: isDrawing ? "none" : "auto",
              borderRadius: isBlock ? 1 : 0,
              opacity: isBlock ? 0.4 : 1,
              "&:hover": { opacity: isBlock ? 0.6 : 1 },
              display: isPreview && isBlock ? "none" : "block",
            }}
            onClick={onClick}
          />
        </Tooltip>
      ) : (
        field.type === "PDFLine" && (
          <Box
            onClick={onClick}
            sx={{
              padding: "4px",
              position: "absolute",
              left: `${rect.left}px`,
              top: `${rect.top}px`,
              zIndex: 55,
              pointerEvents: isDrawing ? "none" : "auto",
            }}
          >
            <Box
              sx={{
                width: `${rect.width}px`,
                height: `${field?.lineWidth ?? 1}px`,
                backgroundColor: "#000000",
              }}
            />
          </Box>
        )
      )}
      <Dialog
        open={openUnassignFieldDialog}
        onClose={handleCloseUnassignFieldDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unassign Field</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to unassign field?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnassignFieldDialog} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleUnassign} variant="contained" color="error">
            Unassign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FieldOverlay;
